import { useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Store,
  Cable,
  Route,
  ArrowDownToLine,
  ArrowUpFromLine,
  Landmark,
  Webhook,
  ShieldAlert,
  LifeBuoy,
  Wallet,
  Coins,
  Banknote,
  ScrollText,
  SlidersHorizontal,
  LogOut,
  Moon,
  Sun,
  Menu,
  BookOpen,
  Building2,
  Package,
  FileText,
  Percent,
  TrendingUp,
  HandCoins,
  Users,
  Globe,
} from "lucide-react";
import { useAuthStore } from "@/stores/auth-store";
import { useTheme } from "@/providers/theme-provider";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { BrandRow } from "@/components/brand";
import { NotificationBell } from "@/components/notification-bell";

/**
 * Sidebar grouped by what an operator is trying to do, rather than one flat
 * list of 22 links. `group` is presentation only - role filtering below still
 * works off the same flat entries, so permissions are unchanged.
 *
 * Labels disambiguated while regrouping: the two "Routing" entries now say what
 * they actually control, and the two Coins icons ("Collector Wallets" vs
 * "Wallets") are separated into their own sections.
 */
const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, group: "Overview" },
  { to: "/visitor-analytics", label: "Visitor analytics", icon: Users, group: "Overview" },

  { to: "/transactions", label: "Transactions", icon: ArrowDownToLine, group: "Money" },
  { to: "/provider-balances", label: "Balance to collect", icon: Wallet, group: "Money" },
  { to: "/settlements", label: "Settlements", icon: Landmark, group: "Money" },
  { to: "/withdrawals", label: "Withdrawals", icon: Banknote, group: "Money" },
  { to: "/wallets", label: "Merchant wallets", icon: Coins, group: "Money" },

  { to: "/merchants", label: "Merchants", icon: Store, group: "Merchants" },
  { to: "/country-profiles", label: "Country profiles", icon: Globe, group: "Merchants" },
  { to: "/connect-accounts", label: "Connected accounts", icon: Wallet, group: "Merchants" },

  { to: "/providers", label: "Providers", icon: Cable, group: "Routing" },
  { to: "/routing", label: "Routing center", icon: Route, group: "Routing" },
  { to: "/routing/merchants", label: "Per-merchant rules", icon: Route, group: "Routing" },

  { to: "/collectors", label: "Manual collection", icon: HandCoins, group: "Collections" },
  { to: "/collector-wallets", label: "Collector wallets", icon: Coins, group: "Collections" },

  { to: "/webhooks", label: "Webhooks", icon: Webhook, group: "Operations" },
  { to: "/disputes", label: "Disputes", icon: ShieldAlert, group: "Operations" },
  { to: "/support", label: "Support", icon: LifeBuoy, group: "Operations" },

  { to: "/subscribers", label: "Subscribers", icon: Building2, superOnly: true, group: "White-label" },
  { to: "/plans", label: "Plans", icon: Package, superOnly: true, group: "White-label" },
  { to: "/invoices", label: "Invoices", icon: FileText, superOnly: true, group: "White-label" },
  { to: "/commission", label: "Commission", icon: Percent, superOnly: true, group: "White-label" },
  { to: "/revenue", label: "Revenue", icon: TrendingUp, superOnly: true, group: "White-label" },

  { to: "/system-config", label: "Settings", icon: SlidersHorizontal, group: "System" },
  { to: "/audit-logs", label: "Audit log", icon: ScrollText, group: "System" },
];

/** Section order in the sidebar. */
const NAV_GROUPS = ["Overview", "Money", "Merchants", "Routing", "Collections", "Operations", "White-label", "System"];

/**
 * Exact match, or a real path segment below it. Plain startsWith would light up
 * "/routing" while on "/routing/merchants", so two links looked active at once.
 */
function isActivePath(pathname: string, to: string) {
  return pathname === to || pathname.startsWith(to + "/");
}

// Polished, human-friendly API reference (not the raw Swagger).
const DOCS_URL = "https://bitpays.live/docs/";

// Isolated environments: Live = admin.bitpays.live, Test = admin.staging.bitpays.live.
// Each has its own database + admin login, so the "switch" navigates between the two
// consoles (preserving the current path) rather than toggling data in place.
const LIVE_ADMIN = "https://admin.bitpays.live";
const TEST_ADMIN = "https://admin.staging.bitpays.live";
const IS_TEST_ENV = typeof window !== "undefined" && /staging/i.test(window.location.hostname);
function currentPath() {
  return typeof window !== "undefined" ? window.location.pathname + window.location.search : "/";
}

function EnvSwitch() {
  const path = currentPath();
  const cell = "inline-flex items-center rounded-md px-2.5 py-0.5 text-[11px] font-bold transition-colors";
  return (
    <div className="ml-1 hidden items-center rounded-lg border p-0.5 sm:inline-flex" title="Switch environment (separate login per environment)">
      <a href={`${LIVE_ADMIN}${path}`} className={cn(cell, !IS_TEST_ENV ? "bg-emerald-500/15 text-emerald-600" : "text-muted-foreground hover:text-foreground")}>Live</a>
      <a href={`${TEST_ADMIN}${path}`} className={cn(cell, IS_TEST_ENV ? "bg-amber-500/20 text-amber-600" : "text-muted-foreground hover:text-foreground")}>Test</a>
    </div>
  );
}
function TestBanner() {
  if (!IS_TEST_ENV) return null;
  return (
    <div className="bg-amber-500/15 py-1 text-center text-[11px] font-semibold text-amber-700 dark:text-amber-400">
      TEST MODE · staging environment — orders &amp; data here are sandbox and separate from Live
    </div>
  );
}

/** BitPays admin shell — dark sidebar (static on desktop, slide-in drawer on mobile). */
export function DashboardLayout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { admin, logout } = useAuthStore();
  const { theme, toggle } = useTheme();
  const [open, setOpen] = useState(false);

  // Provider-admins are scoped to disputes only (backend enforces this too).
  const isProvider = admin?.role === "PROVIDER_ADMIN";
  const isSuper = admin?.role === "SUPER_ADMIN";
  // White-label subscriber admins see only their own tenant's data views; the
  // backend default-denies + tenant-scopes these (Phase 9).
  const isSubscriber = (admin?.role ?? "").startsWith("SUBSCRIBER_");
  // Operational menus a subscriber gets — all tenant-scoped to their own data.
  // Owner-only sections (Subscribers/Plans/Commission/Revenue + Providers/Routing/
  // Accounts/System-Config) are intentionally excluded.
  const SUBSCRIBER_NAV = new Set([
    "/dashboard",
    "/merchants",
    "/transactions",
    "/routing",
    "/routing/merchants",
    "/settlements",
    "/withdrawals",
    "/webhooks",
    "/disputes",
    "/support",
  ]);
  const nav = isProvider
    ? NAV.filter((n) => n.to === "/disputes")
    : isSubscriber
      ? NAV.filter((n) => SUBSCRIBER_NAV.has(n.to))
      : NAV.filter((n) => !("superOnly" in n && n.superOnly) || isSuper);

  const sidebar = (
    <>
      <div className="px-2 pb-5">
        <BrandRow on="dark" />
      </div>
      <nav className="flex flex-1 flex-col gap-0.5 overflow-y-auto">
        {NAV_GROUPS.map((groupName) => {
          const items = nav.filter((n) => ("group" in n ? n.group : undefined) === groupName);
          if (items.length === 0) return null;
          return (
            <div key={groupName} className="mb-1">
              {/* A single-item Overview needs no header. */}
              {groupName !== "Overview" && (
                <div className="px-3 pb-1 pt-3 text-[10.5px] font-semibold uppercase tracking-wider text-sidebar-foreground/35">
                  {groupName}
                </div>
              )}
              {items.map((item) => {
                const active = isActivePath(pathname, item.to);
                const Icon = item.icon;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setOpen(false)}
                    className={cn(
                      "group flex items-center gap-3 rounded-lg px-3 py-2 text-[13.5px] font-medium transition-colors",
                      active
                        ? "bg-white/10 text-white ring-1 ring-white/10 shadow-sm"
                        : "text-sidebar-foreground/70 hover:bg-white/[0.07] hover:text-white"
                    )}
                  >
                    <Icon size={18} className={cn("shrink-0 transition-colors", active ? "text-primary" : "text-sidebar-foreground/50 group-hover:text-primary")} /> {item.label}
                  </Link>
                );
              })}
            </div>
          );
        })}
      </nav>
      <div className="rounded-lg bg-white/5 px-3 py-2.5 text-[11px] text-sidebar-foreground/60">
        bitpays.live · {isProvider ? "Provider" : "Admin"}
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden w-60 shrink-0 flex-col bg-sidebar px-3 py-5 text-sidebar-foreground md:flex">
        {sidebar}
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 flex h-full w-64 flex-col bg-sidebar px-3 py-5 text-sidebar-foreground shadow-xl">
            {sidebar}
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <TestBanner />
        <header className="flex h-14 items-center justify-between border-b bg-card/60 px-4 backdrop-blur md:px-6">
          <div className="flex items-center gap-2">
            <button
              className="-ml-1 rounded-md p-1.5 text-muted-foreground hover:bg-muted md:hidden"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
            >
              <Menu size={19} />
            </button>
            <span className="text-[13px] font-medium text-muted-foreground">Admin Console</span>
            <EnvSwitch />
          </div>
          <div className="flex items-center gap-1.5">
            <a href={DOCS_URL} target="_blank" rel="noopener noreferrer" title="API documentation">
              <Button variant="outline" size="sm"><BookOpen size={15} /> <span className="hidden sm:inline">API Docs</span></Button>
            </a>
            {!isProvider && <NotificationBell />}
            <Button variant="ghost" size="icon" onClick={toggle} aria-label="Toggle theme">
              {theme === "dark" ? <Sun size={17} /> : <Moon size={17} />}
            </Button>
            <div className="hidden h-7 w-7 items-center justify-center rounded-full bp-gradient text-[11px] font-bold text-white sm:flex">
              {(admin?.name ?? "A").charAt(0).toUpperCase()}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                logout();
                navigate("/login");
              }}
            >
              <LogOut size={15} /> <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </header>
        <main className="min-w-0 flex-1 p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
